import React from "react";

import {
  Checkbox,
  FormControl,
  FormControlProps,
  FormHelperText,
  InputLabel,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
  SelectProps as MuiSelectProps,
  SelectChangeEvent,
  SxProps,
  Theme
} from "@mui/material";
import { SelectInputProps } from "@mui/material/Select/SelectInput";

type Value = any | string | string[] | undefined;
type ItemRole = "item" | "category";

const selectAllCss: SxProps = {
  borderBottom: "1px solid #c4cdd1",
  position: "sticky",
  top: 0,
  background: "inherit",
  boxShadow: "0 4px 6px -4px rgb(0 0 0 / 15%)",
  zIndex: 1,
  "&:hover": {
    background: "inherit"
  }
};

export type SelectOption<T = string | number> = {
  key: string;
  value: T;
  text: string;
  role?: ItemRole;
};

type SelectProps<T = unknown> = MuiSelectProps<T> & {
  items: Array<SelectOption | string>;
  label: string;
  value: Value;
  multiple?: boolean;
  selectAll?: boolean;
  renderValue?: (value: Value) => string;
  formProps?: FormControlProps;
  inputLabelSx?: SxProps<Theme>;
  error?: boolean;
  helperText?: string;
  input?: React.ReactElement<any, any>;
};

interface SelectOptionProps {
  key: string;
  value: string | number;
  text: string;
  role: ItemRole;
  multiple: boolean;
  checked?: boolean;
}

// Cannot be a Component. Select children must be MenuItem Component in order to be selectable
const selectOption: React.FC<SelectOptionProps> = (props: SelectOptionProps) => {
  switch (props.role) {
    case "category":
      return <ListSubheader key={props.key}>{props.text}</ListSubheader>;
    case "item":
    default:
      return (
        <MenuItem key={props.key} value={props.value}>
          {props.multiple && <Checkbox color="primary" checked={props.checked} />}
          <ListItemText>{props.text}</ListItemText>
        </MenuItem>
      );
  }
};

export const Select = <T = unknown,>(props: SelectProps<T>) => {
  const { items, selectAll, variant, onChange, formProps, error, helperText, inputLabelSx, fullWidth, ...selectProps } =
    props;
  const renderValue = (value: Value): string => {
    const values = Array.isArray(value) ? value : [value];
    let isValue: Value | undefined;
    return values
      .map((selected: Value) => {
        isValue = items.find((item: Value) => (typeof item === "string" ? item === selected : item.value === selected));
        return typeof isValue === "string" ? isValue : isValue?.text;
      })
      .join(", ");
  };

  const handleChange: SelectInputProps<T>["onChange"] = (e: SelectChangeEvent<T>, child) => {
    if (Array.isArray(e.target.value) && e.target.value.includes("$$SELECT_ALL$$")) {
      if (props.value.length === items.length) {
        e.target.value = [] as unknown as T;
      } else {
        e.target.value = items.map((item) => (typeof item === "string" ? item : item.value)) as unknown as T;
      }
    }
    onChange?.(e, child);
  };

  return (
    <FormControl
      style={{
        maxWidth: "100%"
        // , minWidth: "50%"
      }}
      variant={variant || "standard"}
      fullWidth={fullWidth}
      {...formProps}
    >
      {props.label && (
        <InputLabel shrink sx={inputLabelSx}>
          {props.label}
        </InputLabel>
      )}
      <MuiSelect<T>
        // sx={{
        //   minWidth: 120,
        // }}
        /*@ts-ignore*/
        // renderValue={props.renderValue || renderValue}
        renderValue={props.renderValue || renderValue}
        onChange={handleChange}
        fullWidth={fullWidth}
        {...(props.input ? { input: props.input } : null)}
        {...selectProps}
      >
        {selectAll && props.multiple && (
          <MenuItem sx={selectAllCss} value={"$$SELECT_ALL$$"}>
            <Checkbox color="primary" checked={props.value.length === items.length} />
            <ListItemText>Select all</ListItemText>
          </MenuItem>
        )}
        {items.map((item) => {
          const { key, value, text, role } =
            typeof item === "object" ? item : { key: item, value: item, text: item, role: "item" };
          return selectOption({
            key: key || String(value),
            value,
            text,
            role: role as ItemRole,
            multiple: props.multiple || false,
            checked: props.multiple && props.value.indexOf(value) > -1
          });
        })}
      </MuiSelect>
      {error && <FormHelperText error>{helperText}</FormHelperText>}
    </FormControl>
  );
};
